import { candidacyStatusToLabel, candidacyStatusToRank } from "@/lib/constants";
import {
  CandidacyStatus,
  MyCandidaciesGroupBy,
  MyCandidacyResponse,
} from "@/types";

interface MyCandidaciesDisplayGroup {
  key: string;
  candidacies: MyCandidacyResponse[];
}

export const aviary = {
  // Groups candidacies for MyAviaryTable: Job group sorts alphabetically by
  // jobTitle, Status group by pipeline rank. No groupBy returns a single "all" group.
  getMyCandidaciesDisplayGroups: (
    myCandidacies: MyCandidacyResponse[],
    groupBy?: MyCandidaciesGroupBy,
  ): MyCandidaciesDisplayGroup[] => {
    if (!groupBy) {
      return [{ key: "all", candidacies: myCandidacies }];
    }

    const grouped = myCandidacies.reduce<Record<string, MyCandidacyResponse[]>>(
      (acc, candidacy) => {
        const key =
          groupBy === MyCandidaciesGroupBy.Job
            ? candidacy.jobId
            : candidacy.status;
        acc[key] = [...(acc[key] ?? []), candidacy];
        return acc;
      },
      {},
    );

    const sortedEntries = Object.entries(grouped).sort(([keyA], [keyB]) => {
      if (groupBy === MyCandidaciesGroupBy.Job) {
        const titleA = grouped[keyA][0].jobTitle.toLowerCase();
        const titleB = grouped[keyB][0].jobTitle.toLowerCase();
        return titleA.localeCompare(titleB);
      }
      const rankA = candidacyStatusToRank[keyA as CandidacyStatus];
      const rankB = candidacyStatusToRank[keyB as CandidacyStatus];
      return rankA - rankB;
    });

    return sortedEntries.map(([key, candidacies]) => ({ key, candidacies }));
  },

  // Flat list in display order — drives sheet prev/next in MyAviaryPage.
  flattenMyCandidaciesForNav: (
    myCandidacies: MyCandidacyResponse[],
    groupBy?: MyCandidaciesGroupBy,
  ): MyCandidacyResponse[] => {
    return aviary
      .getMyCandidaciesDisplayGroups(myCandidacies, groupBy)
      .flatMap((group) => group.candidacies);
  },

  // Header text for a display group — "jobTitle — companyName" when grouped
  // by job, status label when grouped by status.
  getGroupTitle: (
    group: MyCandidaciesDisplayGroup,
    groupBy: MyCandidaciesGroupBy,
  ): string => {
    if (groupBy === MyCandidaciesGroupBy.Job) {
      const first = group.candidacies[0];
      return `${first.jobTitle} — ${first.companyName}`;
    }
    return candidacyStatusToLabel[group.key as CandidacyStatus];
  },
};
